import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { CopilotConfig } from './copilotConfigSlice';
import { setConfig, setConfigLoading, setConfigError } from './copilotConfigSlice';

export interface GetCopilotConfigRequest {
    appName: string;
    userId?: string;
}

// Mock configuration data - In production, this would come from an API
const getMockCopilotConfig = (appName: string, userName?: string): CopilotConfig => ({
    // Welcome and bot messages
    botWelcomeMessage: `Hello! I'm your Action 360 Agent, here to help you explore and manage requests across Action 360. Here are a few questions to help you get started.`,
    irisBotMessage: 'Sure, we will be loading support to help you Create a Support Ticket.',

    // Suggestions - These would typically come from a database/API
    botSuggestions: [
        'Get Assigned Actions for [Person]',
        'What features are available?',
        'Show me recent updates',
    ],
    promptsSuggestions: [
        'Who is the Lead Project Manager for [Project]?',
        'List projects where [Person] is a project manager.',
        'List the Active Risks that are overdue for projects where [Person] is a project manager.',
    ],

    // UI Configuration
    textBoxLength: 4000,
    suggestionPopupCharacterThreshold: 5,
    suggestionPopupCount: 6,
    promptsToShow: 6,
    searchCharLimit: 2,

    // Feature flags
    isAutoSuggestionEnabled: false,
    isResponseSuggestionEnabled: false,
    isTypewritingOn: false,
    isCreateIncidentButtonVisible: true,
    isCopyButtonVisible: true,
    isVersionVisible: true,
    isPromptButtonVisible: true,
    isPromptCoachmarkVisible: true,
    isSlashCommandEnabled: false,

    // Text labels
    placeholderText: 'Type your message here...',
    promptModalTitle: 'Copilot Agent Prompts Gallery',
    createSupportTicketButtonText: 'Create Support Ticket',
    featureDescription: '',
    applicationDescription: 'An AI assistant ready to do a wide range of tasks',
    featureName: 'Action 360 Agent',

    // Tenant picker options - Available tenants for the picker
    // NL2SQL Supported: AMM Offer Desk, Azure AI Capacity, Azure Sponsorship for ISV, Compete, CoSell, GSI ACO PIR, MACC, MCAPS Help PM, MCAPS Leader RoB, MDC Attach Purple Exception, Microsoft Marketplace, MSecR, Non AI Capacity, Partner Investments, Red Button, Regional Skilling, SGS, Tech RoB
    tenantPickerOptions: [
        { id: 'f15bec0f-2b85-47d7-8939-6565b09ebd89', name: 'tenant', value: 'f15bec0f-2b85-47d7-8939-6565b09ebd89', text: 'all requests', description: 'All Requests', warningText: '' },
        { id: '628691d2-59ef-4452-9f02-1d837497cc9a', name: 'tenant', value: '628691d2-59ef-4452-9f02-1d837497cc9a', text: 'AMM Offer Desk', subText: 'Query AMM Offer Desk actions with natural language', description: 'AMM Offer Desk Requests' },
        { id: '3d6753f0-f836-4e55-8323-049767b02179', name: 'tenant', value: '3d6753f0-f836-4e55-8323-049767b02179', text: 'Azure AI Capacity', subText: 'Query Azure AI Capacity actions with natural language', description: 'Azure AI Capacity Requests' },
        { id: 'af315f0d-0028-49ed-b197-1bbf897d56c8', name: 'tenant', value: 'af315f0d-0028-49ed-b197-1bbf897d56c8', text: 'Azure Sponsorship for ISV', subText: 'Query Azure Sponsorship for ISV actions with natural language', description: 'Azure Sponsorship for ISV Requests' },
        { id: '46cd8037-9f33-43c8-9262-432efd3317c6', name: 'tenant', value: '46cd8037-9f33-43c8-9262-432efd3317c6', text: 'Compete', subText: 'Query Compete actions with natural language', description: 'Compete Requests' },
        { id: 'e4f33c50-d2ff-4b1c-8b1b-21b8ed18bc3d', name: 'tenant', value: 'e4f33c50-d2ff-4b1c-8b1b-21b8ed18bc3d', text: 'CoSell', subText: 'Query CoSell actions with natural language', description: 'CoSell Requests' },
        { id: '17a19c34-f60e-465f-af34-fa82b61cb9b9', name: 'tenant', value: '17a19c34-f60e-465f-af34-fa82b61cb9b9', text: 'ECIF', description: 'ECIF Requests', warningText: 'ECIF natural language query support is coming soon.' },
        { id: 'd5a5c2d1-b8be-4b18-a4c3-6d09e5f0c59c', name: 'tenant', value: 'd5a5c2d1-b8be-4b18-a4c3-6d09e5f0c59c', text: 'GSI ACO PIR', subText: 'Query GSI ACO PIR actions with natural language', description: 'GSI ACO PIR Requests' },
        { id: 'ca7c5a04-59de-48f1-b6b7-48c3acc5dac5', name: 'tenant', value: 'ca7c5a04-59de-48f1-b6b7-48c3acc5dac5', text: 'MaaS', description: 'MaaS Requests', warningText: 'MaaS natural language query support is coming soon.' },
        { id: '6f76ed48-1b55-4951-920a-90b995e4d5dd', name: 'tenant', value: '6f76ed48-1b55-4951-920a-90b995e4d5dd', text: 'MACC Unified Purple Empowerment', subText: 'Query MACC actions', description: 'MACC Unified Purple Empowerment Requests' },
        { id: '3f9c2a7e-6b4a-4f8a-9e7d-1c5b6a8e42d9', name: 'tenant', value: '3f9c2a7e-6b4a-4f8a-9e7d-1c5b6a8e42d9', text: 'MEA Bedrock', description: 'MEA Bedrock Requests', warningText: 'MEA Bedrock natural language query support is coming soon.' },
        { id: '1762e166-b734-43de-a37d-63aadd76becc', name: 'tenant', value: '1762e166-b734-43de-a37d-63aadd76becc', text: 'MCAPS Help PM', subText: 'Query MCAPS Help actions with natural language', description: 'MCAPS Help PM Requests' },
        { id: '6f76ed48-1b55-4951-920a-90b995e4d5cc', name: 'tenant', value: '6f76ed48-1b55-4951-920a-90b995e4d5cc', text: 'MCAPS Leader RoB', subText: 'Query MCAPS Leader RoB actions with natural language', description: 'MCAPS Leader RoB Requests' },
        { id: '1e3033d1-ae10-4d59-99b9-b7a6dd6e30d4', name: 'tenant', value: '1e3033d1-ae10-4d59-99b9-b7a6dd6e30d4', text: 'MDC Attach Purple Exception', subText: 'Query MDC Attach Purple Exception actions with natural language', description: 'MDC Attach Purple Exception Requests' },
        { id: 'f9e0f41d-707c-46af-b24b-626dc68601ce', name: 'tenant', value: 'f9e0f41d-707c-46af-b24b-626dc68601ce', text: 'Microsoft Marketplace', subText: 'Query Microsoft Marketplace actions with natural language', description: 'Microsoft Marketplace Requests' },
        { id: 'bdbf05d2-ea1a-4d40-952f-41cd5d9bb8d6', name: 'tenant', value: 'bdbf05d2-ea1a-4d40-952f-41cd5d9bb8d6', text: 'Microsoft Federal', description: 'Microsoft Federal Requests', warningText: 'Microsoft Federal natural language query support is coming soon.' },
        { id: 'd6d337f6-2ff5-4d52-9a74-5d9dad09164b', name: 'tenant', value: 'd6d337f6-2ff5-4d52-9a74-5d9dad09164b', text: 'Microsoft Security Research', subText: 'Query Microsoft Security Research actions with natural language', description: 'Microsoft Security Research Requests' },
        { id: '2b6b4852-8108-4aef-afca-d951b9fade7c', name: 'tenant', value: '2b6b4852-8108-4aef-afca-d951b9fade7c', text: 'Non AI Capacity', subText: 'Query Non AI Capacity actions with natural language', description: 'Non AI Capacity Requests' },
        { id: '4b9ac222-75ab-4566-a4e4-c9553896f290', name: 'tenant', value: '4b9ac222-75ab-4566-a4e4-c9553896f290', text: 'Partner Investments', subText: 'Query Partner Investments actions with natural language', description: 'Partner Investments Requests' },
        { id: 'af315f0d-0028-49ed-b197-1aaf897d56c8', name: 'tenant', value: 'af315f0d-0028-49ed-b197-1aaf897d56c8', text: 'Partner Marketing Yield Tracker', description: 'Partner Marketing Yield Tracker Requests', warningText: 'Partner Marketing Yield Tracker natural language query support is coming soon.' },
        { id: 'b878ed1d-5d4c-4a64-9716-221f9484f381', name: 'tenant', value: 'b878ed1d-5d4c-4a64-9716-221f9484f381', text: 'Red Button', subText: 'Query Red Button actions with natural language', description: 'Red Button Requests' },
        { id: '7e4d3c2b-1a9f-4e8d-9c6b-5a4f3e2d1c0b', name: 'tenant', value: '7e4d3c2b-1a9f-4e8d-9c6b-5a4f3e2d1c0b', text: 'Regional Skilling', subText: 'Query Regional Skilling actions with natural language', description: 'Regional Skilling Requests' },
        { id: '5461fe99-0356-49d5-be95-63cbbe8a187a', name: 'tenant', value: '5461fe99-0356-49d5-be95-63cbbe8a187a', text: 'SGS', subText: 'Query SGS actions with natural language', description: 'SGS Requests' },
        { id: 'fdd1b0d7-b70a-4e73-b4a3-de6ac7ba3f5b', name: 'tenant', value: 'fdd1b0d7-b70a-4e73-b4a3-de6ac7ba3f5b', text: 'Tech RoB', subText: 'Query Tech RoB actions with natural language', description: 'Tech RoB Requests' },        
    ],
});

// RTK Query API for Copilot Configuration
export const copilotConfigApi = createApi({
    reducerPath: 'copilotConfigApi',
    baseQuery: fetchBaseQuery({
        baseUrl: '',
        prepareHeaders: (headers) => {
            const token = sessionStorage.getItem('jwtTokenForApi');
            if (token) {
                headers.set('Authorization', `Bearer ${token}`);
            }
            headers.set('Content-Type', 'application/json');
            return headers;
        },
    }),
    tagTypes: ['CopilotConfig'],
    endpoints: (builder) => ({
        // GET /api/copilot/config - Returns mock config since API endpoint is not available
        getCopilotConfig: builder.query<CopilotConfig, GetCopilotConfigRequest>({
            queryFn: async ({ appName, userId }, { dispatch }) => {
                try {
                    dispatch(setConfigLoading(true));

                    // Return mock configuration
                    // In production, this would make an actual API call:
                    // const baseUrl = `${UATConstants.CommonServicesWebApiUrl}/api/copilot`;
                    // return baseQuery({ url: `${baseUrl}/config?appName=${appName}` });
                    const config = getMockCopilotConfig(appName, userId);

                    // Dispatch to Redux store
                    dispatch(setConfig(config));

                    return { data: config };
                } catch (error) {
                    const errorMessage = error instanceof Error ? error.message : 'Failed to load config';
                    dispatch(setConfigError(errorMessage));
                    return { error: { status: 500, data: errorMessage } };
                }
            },
            providesTags: ['CopilotConfig'],
            // Cache for 1 hour - config doesn't change often
            keepUnusedDataFor: 3600,
        }),
    }),
});

// Export hooks for usage in components
export const {
    useGetCopilotConfigQuery,
    useLazyGetCopilotConfigQuery,
} = copilotConfigApi;
// Generated by GitHub Copilot | Updated on: 2026-08-07 | 1 line modified | Added Marketplace unsupported query banner
// Generated by GitHub Copilot | Updated on: 2026-09-15 | 1 line removed | Removed duplicate unsupported MSecR picker entry
